import { useState, useCallback, useEffect } from 'react';

/**
 * 사진 상세조회 라이트박스 — 확대해서 크게 보기 + 좌우 이동 + 닫기 (축소 스코프).
 * 줌/팬/회전 등은 범위 밖 (표준문서 999 참고, 1:1 문의 첨부사진 등 단순 열람 용도).
 */
interface Props {
  images: string[];
  initialIndex: number;
  onClose: () => void;
}

export function ImageLightbox({ images, initialIndex, onClose }: Props) {
  const [index, setIndex] = useState(initialIndex);

  const prev = useCallback(() => setIndex((i) => Math.max(0, i - 1)), []);
  const next = useCallback(() => setIndex((i) => Math.min(images.length - 1, i + 1)), [images.length]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, prev, next]);

  useEffect(() => {
    const saved = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = saved; };
  }, []);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="첨부이미지 보기"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      className="fixed inset-0 z-modal flex items-center justify-center bg-[var(--overlay-scrim-strong)]"
    >
      {/* 닫기 */}
      <button
        type="button"
        onClick={onClose}
        aria-label="닫기"
        className="absolute right-5 top-5 flex h-9 w-9 items-center justify-center rounded-full bg-white/15 text-white transition-colors hover:bg-white/25"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
          <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>

      {/* 카운터 */}
      {images.length > 1 && (
        <span className="absolute left-1/2 top-6 -translate-x-1/2 rounded-full bg-[var(--overlay-scrim)] px-3 py-1 text-xs font-semibold text-white">
          {index + 1} / {images.length}
        </span>
      )}

      {/* 이전 */}
      {index > 0 && (
        <button
          type="button"
          onClick={prev}
          aria-label="이전 사진"
          className="absolute left-4 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/15 text-white transition-colors hover:bg-white/25"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
      )}

      {/* 사진 */}
      <img
        src={images[index]}
        alt={`첨부이미지 ${index + 1}`}
        onClick={(e) => e.stopPropagation()}
        className="max-h-[85vh] max-w-[85vw] rounded-lg object-contain shadow-2xl"
      />

      {/* 다음 */}
      {index < images.length - 1 && (
        <button
          type="button"
          onClick={next}
          aria-label="다음 사진"
          className="absolute right-4 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/15 text-white transition-colors hover:bg-white/25"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>
      )}
    </div>
  );
}
